import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useIsSameOrChildUrl } from "../../../helpers/UrlHelper";
import { useNonModalLocation } from "../../Utils/routing";

export const ComplexMenuContext = createContext();

export const useComplexMenuProps = navigationItems => {
  const [activeSubMenuId, setActiveSubMenuId] = useState();
  const [hoveredSubMenuId, setHoveredSubMenuId] = useState(null);

  const handleNavigationItemActivated = useCallback(
    id => {
      const newSubMenuId = id === activeSubMenuId ? null : id;
      setActiveSubMenuId(newSubMenuId);
    },
    [activeSubMenuId]
  );

  const activeNavigationItem = navigationItems.find(
    navigationItem => navigationItem.id === activeSubMenuId
  );

  const activeSubMenu = activeNavigationItem?.children?.length
    ? activeNavigationItem
    : null;

  const { pathname: currentPath } = useNonModalLocation();

  // This creates a flat array of the navigation item urls from the complete menu tree
  const navigationItemUrls = useMemo(
    () =>
      navigationItems.reduce((urlsList, { url, children }) => {
        const urls = [];
        if (url) urls.push(url);
        children?.forEach(({ url }) => url && urls.push(url));

        return [...urlsList, ...urls];
      }, []),
    [navigationItems]
  );

  const isSameOrChildUrl = useIsSameOrChildUrl();

  // This allows closing an open submenu if the page changes and doesn't match any menu
  // or submenu items.
  //
  // TODO : When we build the 3rd level menu, we'll need to handle page changes here
  //
  useEffect(() => {
    const hasActiveItem = navigationItemUrls.some(path =>
      isSameOrChildUrl(path, currentPath)
    );

    if (!hasActiveItem) setActiveSubMenuId(null);
  }, [currentPath, isSameOrChildUrl, navigationItemUrls]);

  return {
    activeSubMenu,
    activeNavigationItem,
    handleNavigationItemActivated,
    hoveredSubMenuId,
    setHoveredSubMenuId
  };
};

export const useComplexMenuContext = () => {
  return useContext(ComplexMenuContext);
};

export const useNavigationItemIsActive = navigationItem => {
  const { id, url } = navigationItem;
  const { activeNavigationItem, handleNavigationItemActivated } = useComplexMenuContext();
  const isSameOrChildUrl = useIsSameOrChildUrl();

  const [isInitialized, setIsInitialized] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const { pathname: currentPath } = useNonModalLocation();

  const handleClick = useCallback(() => {
    setIsActive(!isActive);
    handleNavigationItemActivated(id);
  }, [handleNavigationItemActivated, id, isActive]);

  // Ensure that when the current navigation item is active, and another sub menu is
  // selected, we disable it so it doesn't appear to be active even if it's not anymore.
  useEffect(() => {
    if (!activeNavigationItem) return;

    if (activeNavigationItem.id !== id && isActive) {
      setIsActive(false);
    }
  }, [activeNavigationItem, id, isActive]);

  // Whenever a `updateActiveState` dependency is updated, recalculate the current active
  // state of the item, but don't force updating it if `isActive` was already in the
  // calculated state.
  useEffect(() => {
    const newIsActive =
      isSameOrChildUrl(url, currentPath) ||
      (navigationItem.children || []).some(({ url }) =>
        isSameOrChildUrl(url, currentPath)
      );

    // Since the `handleNavigationItemActivated` callback changes whenever the active sub
    // menu id is changed, we ensure that we don't create a state update loop by not
    // re-updating the same value below.
    if (isActive !== newIsActive) {
      setIsActive(newIsActive);

      // If a page change makes the navigation item active, set it as active in the parent
      // menu
      if (newIsActive) handleNavigationItemActivated(id);
    }

    if (!isInitialized) setIsInitialized(true);

    // Note that we disable the react exhaustive deps ESLint rule here as we don't want
    // the effect to be re-run when `isActive` or `handleNavigationItemActivated` change,
    // to only react to currentPath changes as it's only meant to enforce the menu state
    // when the current page is changed.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  return { isActive, handleClick, isInitialized };
};
